import React, { useState, useEffect, useRef } from "react";
import adminService from "../../api/services/adminService";

const inputClass =
  "w-full p-2.5 text-xs md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700 min-w-0";

const CURRENCIES = ["PKR", "AED", "USD", "EUR", "GBP"];

export default function ManualPurchaseSection({ onSuccess }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [currency, setCurrency] = useState("PKR");
  const [customerId, setCustomerId] = useState("");
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminService.getSubscriptionPlans();
        if (!cancelled && res?.plans?.length) {
          setPlans(res.plans);
          if (!subscriptionId && res.plans[0]?.id) setSubscriptionId(String(res.plans[0].id));
        }
      } catch (e) {
        if (!cancelled) setError("Failed to load subscription plans");
      } finally {
        if (!cancelled) setLoadingPlans(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const res = await adminService.searchUsersForSubscription(searchQuery.trim());
        setSearchResults(res?.users || []);
        setShowDropdown(true);
      } catch (e) {
        setSearchResults([]);
        setError(e?.message || "Search failed");
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchQuery]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchQuery(user.email || "");
    setShowDropdown(false);
    setSearchResults([]);
    setError(null);
  };

  const handleClearUser = () => {
    setSelectedUser(null);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    const email = selectedUser?.email || searchQuery.trim();
    if (!email) {
      setError("Select or enter a user email.");
      return;
    }
    const amt = parseFloat(amount);
    if (!amount || isNaN(amt) || amt <= 0) {
      setError("Enter a valid amount.");
      return;
    }
    const planId = subscriptionId ? parseInt(subscriptionId, 10) : null;
    if (!planId || !plans.some((p) => p.id === planId)) {
      setError("Select a subscription plan.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        email,
        amount: amt,
        id: planId,
        currency,
      };
      if (customerId.trim()) payload.customer_id = customerId.trim();
      const res = await adminService.purchaseSubscriptionByEmail(payload);
      setSuccessMessage(res?.message || "Subscription purchased successfully.");
      setAmount("");
      setCustomerId("");
      setSelectedUser(null);
      setSearchQuery("");
      onSuccess?.();
    } catch (err) {
      setError(err?.message || err?.response?.data?.message || "Purchase failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-6">
      <div className="p-5 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-white">
        <h2 className="text-lg sm:text-xl font-bold text-gray-800 tracking-tight">
          Manual subscription purchase
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Search user by name, email, or phone; select the user and add amount to purchase subscription.
        </p>
      </div>
      <div className="p-5 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-4 max-w-2xl">
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Search user (name, email, or phone)</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              placeholder="Type to search..."
              className={inputClass}
            />
            {selectedUser && (
              <button
                type="button"
                onClick={handleClearUser}
                className="absolute right-2 top-9 text-gray-400 hover:text-red-600 text-sm"
              >
                Clear
              </button>
            )}
            {searching && (
              <p className="mt-1 text-xs text-gray-500">Searching...</p>
            )}
            {showDropdown && searchResults.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg py-1">
                {searchResults.map((u) => (
                  <li key={u.id}>
                    <button
                      type="button"
                      onClick={() => handleSelectUser(u)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                    >
                      <span className="font-medium text-gray-900">
                        {u.firstName} {u.lastName}
                      </span>
                      <span className="text-gray-500 block truncate">{u.email}</span>
                      {u.contactNo && <span className="text-gray-400 text-xs">{u.contactNo}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Amount</label>
            <input
              type="number"
              step="any"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Subscription plan</label>
            <select
              value={subscriptionId}
              onChange={(e) => setSubscriptionId(e.target.value)}
              className={inputClass}
              disabled={loadingPlans}
            >
              <option value="">Select plan</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.duration_days ? `(${p.duration_days} days)` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className={inputClass}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Customer ID (optional)</label>
            <input
              type="text"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="e.g. RevenueCat customer ID"
              className={inputClass}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
          {successMessage && (
            <p className="text-sm text-green-600">{successMessage}</p>
          )}

          <button
            type="submit"
            disabled={submitting || loadingPlans}
            className="px-4 sm:px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-semibold shadow-md transition-colors"
          >
            {submitting ? "Purchasing..." : "Purchase subscription"}
          </button>
        </form>
      </div>
    </div>
  );
}
